import React from "react";
import { DateTime } from "luxon";
import {
  manualInvoiceUploadAnalyzeResponseSchema,
} from "compta-shared";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import AutoComplete from "../../utils/components/AutoCompleteComp/AutoComplete2.tsx";
import DateInput from "../../utils/components/DateInput.tsx";
import FloatInput from "../../utils/components/FloatInput.tsx";
import DuplicateFactureWarningModal, {
  type DuplicateFactureWarningModalItem,
} from "./DuplicateFactureWarningModal.tsx";
import {
  uploadReducer,
  type UploadItem,
} from "./toPayInvoiceUploadReducer.ts";
import ImportedInvoiceReceipt from "./ImportedInvoiceReceipt.tsx";

const hostDirty = import.meta.env.VITE_DEV_HOST || window.location.host;
const prefix = import.meta.env.MODE === "development" ? "http://" : "https://";
const host = hostDirty.replace("http://", "");
const analyzeUrl = `${prefix}${host}/comptaRest/factures/to-pay/analyze`;

type InvoiceType = "normal" | "resto";

const parseItems = (payload: unknown) => {
  const parsed = manualInvoiceUploadAnalyzeResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error("La réponse de l'analyse OCR est invalide");
  }

  return parsed.data.items.map(
    (item) =>
      ({
        docId: item.docId,
        originalFileName: item.originalFileName,
        pdfUrl: item.pdfUrl,
        emetteur: item.emetteur || "",
        invoiceType: item.invoiceType,
        modeRef: item.modeRef || "",
        montantCent: item.montantCent,
        dateFacture: item.dateFacture
          ? DateTime.fromISO(item.dateFacture).toJSDate()
          : null,
        paymentDueDate: item.paymentDueDate
          ? DateTime.fromISO(item.paymentDueDate).toJSDate()
          : null,
        montantTitresCent: item.montantTitresCent,
        montantFraisCent: item.montantFraisCent,
        error: item.error,
      }) satisfies UploadItem,
  );
};

const getBlockingErrors = (item: UploadItem) => {
  const errors: string[] = [];
  if (!item.docId) {
    errors.push("Document indisponible");
  }
  if (!item.emetteur.trim()) {
    errors.push("Fournisseur obligatoire");
  }
  if (!item.invoiceType) {
    errors.push("Type de facture obligatoire");
  }
  if (!item.modeRef.trim()) {
    errors.push("Référence facture obligatoire");
  }
  if (typeof item.montantCent !== "number") {
    errors.push("Montant TTC obligatoire");
  }
  if (!item.dateFacture) {
    errors.push("Date de facture obligatoire");
  }
  if (
    item.invoiceType === "resto" &&
    (typeof item.montantTitresCent !== "number" ||
      typeof item.montantFraisCent !== "number")
  ) {
    errors.push("Montants titres et frais obligatoires");
  }
  return errors;
};

type ReadyUploadItem = UploadItem & {
  docId: string;
  invoiceType: InvoiceType;
  montantCent: number;
  dateFacture: Date;
};

const isReadyToCreate = (item: UploadItem): item is ReadyUploadItem =>
  getBlockingErrors(item).length === 0 &&
  item.docId !== null &&
  item.invoiceType !== null &&
  typeof item.montantCent === "number" &&
  item.dateFacture !== null;

const ToPayInvoiceUpload = () => {
  const utils = trpc.useUtils();
  const { data: libellés } = trpc.libellé.getAll.useQuery();
  const createDraftMutation =
    trpc.facture.createDraftFromImportedDoc.useMutation();
  const [state, dispatch] = React.useReducer(uploadReducer, {
    items: [],
    selectedItemIndex: 0,
  });
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);
  const [receivedDocIds, setReceivedDocIds] = React.useState<string[]>([]);
  const [isCreating, setIsCreating] = React.useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] =
    React.useState(false);
  const [pendingSubmissionItems, setPendingSubmissionItems] = React.useState<
    ReadyUploadItem[]
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [emetteurSearches, setEmetteurSearches] = React.useState<
    Record<number, string>
  >({});
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [duplicateWarningItems, setDuplicateWarningItems] = React.useState<
    DuplicateFactureWarningModalItem[]
  >([]);

  const selectedItem = state.items[state.selectedItemIndex] || null;
  const readyItems = state.items.filter(isReadyToCreate);
  const isBusy = isCreating || isCheckingDuplicates;

  const getEmetteurCandidates = (search: string) =>
    search
      ? (libellés || [])
          .map((libellé) => libellé.libellé)
          .filter((label) => new RegExp(search, "i").test(label))
      : [];

  const analyzeFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    if (files.length > 20) {
      setError("Vous pouvez importer au maximum 20 PDF à la fois");
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setIsAnalyzing(true);
    const formData = new FormData();
    Array.from(files).forEach((file, index) => {
      formData.append(`file-${index}`, file, file.name);
    });

    try {
      const response = await fetch(analyzeUrl, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("L'analyse des factures a échoué");
      }
      const items = parseItems(await response.json());
      dispatch({ type: "replace", items });
      setReceivedDocIds((current) => [
        ...new Set([
          ...current,
          ...items.flatMap((item) => (item.docId ? [item.docId] : [])),
        ]),
      ]);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "L'analyse des factures a échoué",
      );
    } finally {
      await utils.facture.invalidate();
      setIsAnalyzing(false);
    }
  };

  const updateItem = (index: number, changes: Partial<UploadItem>) => {
    const item = state.items[index];
    if (!item) {
      return;
    }
    dispatch({ type: "update", index, item: { ...item, ...changes } });
  };

  const createDrafts = async (items: ReadyUploadItem[]) => {
    if (items.length === 0) {
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setIsCreating(true);
    try {
      const results = await Promise.allSettled(
        items.map((item) =>
          createDraftMutation.mutateAsync({
            docId: item.docId,
            emetteur: item.emetteur.trim(),
            invoiceType: item.invoiceType,
            modeRef: item.modeRef.trim(),
            chequeNumber: null,
            montantCent: item.montantCent,
            dateFacture: item.dateFacture,
            datePrelevement: null,
            paymentDueDate: item.paymentDueDate,
            montantTitresCent: item.montantTitresCent,
            montantFraisCent: item.montantFraisCent,
          }),
        ),
      );
      const createdDocIds = new Set<string>();
      const failures: string[] = [];
      results.forEach((result, index) => {
        const item = items[index];
        if (result.status === "fulfilled") {
          createdDocIds.add(item.docId);
        } else {
          failures.push(
            `${item.originalFileName}: ${result.reason.message || "erreur"}`,
          );
        }
      });

      if (createdDocIds.size > 0) {
        dispatch({ type: "removeCreated", docIds: createdDocIds });
        setSuccessMessage(
          `${createdDocIds.size} facture${createdDocIds.size > 1 ? "s" : ""} ajoutée${createdDocIds.size > 1 ? "s" : ""} aux règlements en attente`,
        );
      }
      if (failures.length > 0) {
        setError(failures.join("\n"));
      }
    } finally {
      await Promise.all([utils.facture.invalidate(), utils.libellé.invalidate()]);
      setIsCreating(false);
    }
  };

  const checkDuplicatesThenCreate = async () => {
    if (
      readyItems.length === 0 ||
      isBusy ||
      isAnalyzing
    ) {
      return;
    }
    setError(null);
    setIsCheckingDuplicates(true);
    const snapshot = state.items.filter(isReadyToCreate);
    try {
      const warnings = await utils.facture.checkBusinessDuplicatesBatch.fetch({
        items: snapshot.map((item) => ({
          docId: item.docId,
          originalFileName: item.originalFileName,
          modeRef: item.modeRef,
          dateFacture: item.dateFacture,
          montantCent: item.montantCent,
        })),
      });
      const warningItems = warnings
        .filter((warning) => warning.duplicates.length > 0)
        .map((warning) => ({
          title: warning.originalFileName,
          duplicates: warning.duplicates,
        }));
      if (warningItems.length > 0) {
        const duplicateDocIds = new Set(warnings.map((warning) => warning.docId));
        setPendingSubmissionItems(
          snapshot.filter((item) => !duplicateDocIds.has(item.docId)),
        );
        setDuplicateWarningItems(warningItems);
        return;
      }
      await createDrafts(snapshot);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "Erreur pendant la recherche de doublons",
      );
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  const refreshPdfUrl = async (item: UploadItem) => {
    if (!item.docId) {
      return;
    }
    try {
      const pdfUrl = await utils.facture.refreshImportedDocPdfUrl.fetch({
        docId: item.docId,
      });
      const index = state.items.findIndex(
        (candidate) => candidate.docId === item.docId,
      );
      if (index !== -1) {
        updateItem(index, { pdfUrl });
      }
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "Impossible de rafraîchir le PDF",
      );
    }
  };

  return (
    <div className="card">
      <div className="card-header fw-bold">Importer des factures à régler</div>
      <div className="card-body">
        <div className="row align-items-end g-2">
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="to-pay-invoice-files">
              Factures PDF
            </label>
            <input
              id="to-pay-invoice-files"
              className="form-control"
              type="file"
              accept="application/pdf"
              multiple
              disabled={isAnalyzing || isBusy}
              onChange={(event) => {
                void analyzeFiles(event.currentTarget.files);
                event.currentTarget.value = "";
              }}
            />
          </div>
          <div className="col-12 col-md-6 text-muted small">
            Jusqu'à 20 PDF de 10 Mo. Chaque facture peut avoir un fournisseur
            différent.
          </div>
        </div>

        {isAnalyzing ? (
          <div className="alert alert-info mt-3">Analyse OCR en cours...</div>
        ) : null}
        {error ? (
          <div className="alert alert-danger mt-3 text-pre-wrap">{error}</div>
        ) : null}
        {successMessage ? (
          <div className="alert alert-success mt-3">{successMessage}</div>
        ) : null}
        <ImportedInvoiceReceipt documentIds={receivedDocIds} />

        {state.items.length > 0 ? (
          <>
            <div className="table-responsive mt-3">
              <table className="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th>Fichier</th>
                    <th>Fournisseur</th>
                    <th>Type</th>
                    <th>Référence</th>
                    <th>Montant TTC</th>
                    <th>Date</th>
                    <th>Échéance</th>
                    <th>Statut</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {state.items.map((item, index) => {
                    const blockingErrors = getBlockingErrors(item);
                    const isBlocked = item.docId === null;
                    const itemDisabled = isBusy || isBlocked;
                    return (
                      <tr
                        key={`${item.originalFileName}-${index}`}
                        className={
                          state.selectedItemIndex === index
                            ? "table-primary"
                            : ""
                        }
                        onClick={() => dispatch({ type: "select", index })}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{item.originalFileName}</td>
                        <td>
                          <AutoComplete
                            candidates={getEmetteurCandidates(
                              emetteurSearches[index] || "",
                            )}
                            value={item.emetteur}
                            onSetSearch={(value) =>
                              setEmetteurSearches((searches) => ({
                                ...searches,
                                [index]: value || "",
                              }))
                            }
                            onSetValue={(emetteur) =>
                              updateItem(index, { emetteur })
                            }
                            tagsOn={false}
                            acceptNewValues={true}
                            isValid={!!item.emetteur.trim()}
                            invalidMessage="Fournisseur obligatoire"
                            isTouched={true}
                            disabled={itemDisabled}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={item.invoiceType || ""}
                            disabled={itemDisabled}
                            onChange={(event) => {
                              const invoiceType = event.currentTarget.value;
                              updateItem(index, {
                                invoiceType:
                                  invoiceType === "normal" ||
                                  invoiceType === "resto"
                                    ? invoiceType
                                    : null,
                              });
                            }}
                          >
                            <option value="">À choisir</option>
                            <option value="normal">Normale</option>
                            <option value="resto">Resto</option>
                          </select>
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={item.modeRef}
                            disabled={itemDisabled}
                            onChange={(event) =>
                              updateItem(index, {
                                modeRef: event.currentTarget.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <FloatInput
                            valueCent={item.montantCent}
                            disabled={itemDisabled}
                            onValueCentChange={(montantCent) =>
                              updateItem(index, { montantCent })
                            }
                          />
                        </td>
                        <td>
                          <DateInput
                            date={item.dateFacture}
                            disabled={itemDisabled}
                            onDateChange={(dateFacture) =>
                              updateItem(index, { dateFacture })
                            }
                          />
                        </td>
                        <td>
                          <DateInput
                            date={item.paymentDueDate}
                            disabled={itemDisabled}
                            onDateChange={(paymentDueDate) =>
                              updateItem(index, { paymentDueDate })
                            }
                          />
                        </td>
                        <td>
                          {isBlocked ? (
                            <span className="badge text-bg-secondary">
                              Import impossible
                            </span>
                          ) : blockingErrors.length === 0 ? (
                            <span className="badge text-bg-success">Prête</span>
                          ) : (
                            <span className="badge text-bg-danger">
                              À corriger
                            </span>
                          )}
                          {item.error ? (
                            <div className="small text-warning-emphasis">
                              {isBlocked ? "Import" : "OCR"}: {item.error}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={isBusy}
                            onClick={(event) => {
                              event.stopPropagation();
                              dispatch({ type: "remove", index });
                            }}
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {selectedItem?.invoiceType === "resto" ? (
              <div className="row g-2 mt-1">
                <div className="col-12 col-md-3">
                  <label className="form-label">Montant titres</label>
                  <FloatInput
                    valueCent={selectedItem.montantTitresCent}
                    disabled={isBusy}
                    onValueCentChange={(montantTitresCent) =>
                      updateItem(state.selectedItemIndex, { montantTitresCent })
                    }
                  />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Montant frais</label>
                  <FloatInput
                    valueCent={selectedItem.montantFraisCent}
                    disabled={isBusy}
                    onValueCentChange={(montantFraisCent) =>
                      updateItem(state.selectedItemIndex, { montantFraisCent })
                    }
                  />
                </div>
              </div>
            ) : null}

            {selectedItem?.pdfUrl ? (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-muted small">Aperçu du PDF</span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={isBusy}
                    onClick={() => void refreshPdfUrl(selectedItem)}
                  >
                    Rafraîchir le PDF
                  </button>
                </div>
                <iframe
                  className="w-100 border"
                  src={selectedItem.pdfUrl}
                  title={selectedItem.originalFileName}
                  style={{ height: 520 }}
                />
              </div>
            ) : null}

            <button
              type="button"
              className="btn btn-primary mt-3"
              disabled={readyItems.length === 0 || isBusy || isAnalyzing}
              onClick={() => void checkDuplicatesThenCreate()}
            >
              {isCheckingDuplicates
                ? "Vérification des doublons..."
                : isCreating
                  ? "Ajout aux règlements en attente..."
                  : `Ajouter ${readyItems.length} facture(s) aux règlements en attente`}
            </button>
          </>
        ) : null}
      </div>
      <DuplicateFactureWarningModal
        show={duplicateWarningItems.length > 0}
        items={duplicateWarningItems}
        onCancel={() => {
          setDuplicateWarningItems([]);
          setPendingSubmissionItems([]);
        }}
        onConfirm={async () => {
          setDuplicateWarningItems([]);
          const items = pendingSubmissionItems;
          setPendingSubmissionItems([]);
          await createDrafts(items);
        }}
        confirmLabel="Ajouter les factures sans doublon"
        isSubmitting={isCreating}
        blocking={pendingSubmissionItems.length === 0}
        description={
          pendingSubmissionItems.length === 0
            ? "Aucune facture ne peut être ajoutée aux règlements en attente tant que les doublons listés existent."
            : "Les factures en double ne seront pas ajoutées. Vérifie la liste ci-dessous avant d'ajouter les autres factures."
        }
      />
    </div>
  );
};

export default ToPayInvoiceUpload;
